import { useCallback, useEffect, useMemo, useState } from "react";
import FlowModal from "../components/FlowModal.jsx";
import ResultModal from "../components/ResultModal.jsx";
import SettlementPreviewModal from "../components/SettlementPreviewModal.jsx";
import Shimmer from "../components/Shimmer.jsx";
import { StatCard, STAT_ICONS } from "../components/StatRow.jsx";
import config from "../config/index.js";
import { useWallet } from "../context/WalletContext.jsx";
import { useDeposits } from "../hooks/useDeposits.js";
import { useReinvestmentPools } from "../hooks/useReinvestmentPools.js";
import {
    fetchReinvestmentPool,
    fetchReinvestmentSettlements,
    syncReinvestmentPool,
    fetchKeeperFeeEstimate
} from "../services/api.js";
import { getContract } from "../services/contracts.js";
import { getTxOverrides } from "../services/gas.js";
import { decodeContractError } from "../utils/contractError.js";
import { formatUsdcCompact, depositStateLabel } from "../utils/format.js";

const TOKEN_SYMBOL = "USDC";

/**
 * Consolidated Yield Reinvestment (M4).
 *
 * A user creates a Consolidated Reinvestment Pool, attaches their term
 * deposits to it, and the pool DAILY-COMPOUNDS the protocol's 20% rate
 * as deferred on-chain accounting. The compound-over-simple edge is the
 * pool "bonus": it materializes — as a fresh term deposit, or as USDC if
 * below the 100 USDC mint floor — on detach, manual reinvest, or when
 * the underlying deposit is liquidated.
 *
 * Same dark-green / gold visual shell as the Turbo + Lending surfaces.
 */
export default function Reinvest() {
    const { address, signer } = useWallet();
    const { deposits, isLoading: depositsLoading, refresh: refreshDeposits } = useDeposits(address);
    const { pools, isLoading: poolsLoading, refresh: refreshPools } = useReinvestmentPools(address);

    const [selectedPoolId, setSelectedPoolId] = useState(null);
    const [poolDetail, setPoolDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [attachDepositId, setAttachDepositId] = useState(null);
    const [settlements, setSettlements] = useState([]);
    const [busy, setBusy] = useState(false);

    const [flowOpen, setFlowOpen] = useState(false);
    const [flowMeta, setFlowMeta] = useState({ title: "", steps: [] });
    const [result, setResult] = useState(null);
    // Pre-tx settlement preview state. `{ member, mode, poolId }` while
    // a Reinvest/Detach confirmation modal is up, null otherwise.
    // Keeps the dialog and the actual chain submit decoupled: clicking
    // "Reinvest" opens the preview; confirming inside the preview fires
    // the tx via `handleSettle`. Lets the user back out after seeing
    // exactly how much bonus is about to crystallise.
    const [settleTarget, setSettleTarget] = useState(null);

    // §V6.2 — live dynamic keeper-fee estimate. Polled every 30s so the
    // threshold meter / sweep copy quotes the current expected fee
    // (gas × ETH/USD, capped) instead of a stale static $2.
    const [keeperFeeEstimate, setKeeperFeeEstimate] = useState(null);
    useEffect(() => {
        let cancelled = false;
        async function refresh() {
            try {
                const data = await fetchKeeperFeeEstimate();
                if (!cancelled) setKeeperFeeEstimate(data);
            } catch {
                /* keep the last estimate visible; backend hiccup is non-fatal */
            }
        }
        refresh();
        const id = setInterval(refresh, 30_000);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, []);

    // The pool the UI is focused on — explicit selection, else the first.
    const selectedPool = useMemo(
        () => pools.find((p) => p.poolId === selectedPoolId) || pools[0] || null,
        [pools, selectedPoolId]
    );

    // Set of depositIds already attached to ANY of the user's pools — used
    // to filter the "eligible to attach" list.
    const attachedSet = useMemo(() => {
        const set = new Set();
        for (const pool of pools) {
            for (const id of pool.members) set.add(id);
        }
        return set;
    }, [pools]);

    // Eligible-to-attach deposits: Active and not already pooled. That's it.
    //
    // §M6 (Jul 2026 client review) — "This is also foundationally incorrect.
    // The idea of Auto-Reinvest is that you sweep the interest from multiple
    // TD's to create a new TD. Therefore the list on the right should remain
    // the same regardless of whether you select Standard or Turbo. Standard
    // or Turbo only define what the new TD becomes, not the source of the
    // yield to create it." → Action (a): keep the list the same for Standard
    // and Turbo, most recent at the top.
    //
    // This used to also require the deposit's productMode to match the
    // selected pool's type, mirroring the on-chain `PoolTypeMismatch` gate.
    // That gate has been REMOVED from ConsolidatedYieldReinvestment
    // .attachDeposit, so the filter goes with it.
    const eligibleDeposits = useMemo(
        () =>
            deposits
                .filter((d) => {
                    if (depositStateLabel(d.state) !== "Active") return false;
                    if (attachedSet.has(d.id)) return false;
                    return true;
                })
                // Most recent first — a deposit opened minutes ago is the one
                // the user is looking for.
                .sort((a, b) => Number(b.startTime) - Number(a.startTime)),
        [deposits, attachedSet]
    );

    const loadPoolDetail = useCallback(async (poolId) => {
        if (!poolId) {
            setPoolDetail(null);
            return;
        }
        setDetailLoading(true);
        try {
            const data = await fetchReinvestmentPool(poolId);
            setPoolDetail(data);
        } catch {
            // A pool that exists on-chain but isn't indexed yet 404s — keep
            // the shell rendered rather than surfacing a hard error.
            setPoolDetail(null);
        } finally {
            setDetailLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPoolDetail(selectedPool?.poolId);
    }, [selectedPool?.poolId, loadPoolDetail]);

    useEffect(() => {
        if (!address) return;
        fetchReinvestmentSettlements({ limit: 20 })
            .then((data) => setSettlements(data?.items || []))
            .catch(() => setSettlements([]));
    }, [address]);

    /**
     * Polls `refreshPools` until `predicate(latestPoolsArray)` returns
     * true, or `maxMs` elapses. Used after a write tx to wait for the
     * WS listener (or polling-indexer fallback) to surface the new
     * state — without this the immediate post-`tx.wait()` refresh
     * races ahead of indexing and the user sees stale data until the
     * next manual reload.
     *
     * Returns the latest pools array (so callers can chain a
     * `loadPoolDetail` once the predicate is satisfied).
     */
    async function waitForPools(predicate, { intervalMs = 800, maxMs = 12000 } = {}) {
        const start = Date.now();
        let latest = pools;
        while (Date.now() - start < maxMs) {
            latest = await refreshPools();
            if (predicate(latest)) return latest;
            await delay(intervalMs);
        }
        return latest;
    }

    function openFlow(title, steps) {
        setFlowMeta({ title, steps });
        setFlowOpen(true);
        setResult(null);
    }

    function markStep(key, status) {
        setFlowMeta((prev) => ({
            ...prev,
            steps: prev.steps.map((s) => (s.key === key ? { ...s, status } : s))
        }));
    }

    /* --------------------------- actions --------------------------- */

    /**
     * Creates a §I.6 Consolidated Reinvestment Pool of the given type.
     *   poolType = 0 → Standard (yield → plain new deposit on sweep)
     *   poolType = 1 → Turbo    (yield + incremental leverage → 160 USDC
     *                            deposit on sweep)
     */
    async function handleCreatePool(poolType = 0) {
        if (!signer || busy) return;
        setBusy(true);
        const typeLabel = poolType === 1 ? "Turbo" : "Standard";
        openFlow(`Creating ${typeLabel} Pool`, [
            { key: "create", label: `Create ${typeLabel} Reinvestment Pool`, doneLabel: "Pool created", status: "loading" }
        ]);
        try {
            const cyr = getContract("reinvestment", signer);
            const overrides = await getTxOverrides(signer.provider);
            const tx = await cyr.createPool(poolType, overrides);
            const receipt = await tx.wait();
            markStep("create", "done");

            let newPoolId = null;
            for (const log of receipt.logs) {
                try {
                    const parsed = cyr.interface.parseLog(log);
                    if (parsed?.name === "PoolCreated") {
                        newPoolId = Number(parsed.args.poolId);
                        break;
                    }
                } catch {
                    /* not a CYR log */
                }
            }
            await delay(400);
            setFlowOpen(false);
            setResult({
                type: "success",
                title: `${typeLabel} Pool Created`,
                message: newPoolId
                    ? `${typeLabel} Reinvestment Pool #${newPoolId} is ready. Attach a ${typeLabel.toLowerCase()}-mode term deposit to start compounding.`
                    : `Your ${typeLabel} Reinvestment Pool is ready.`
            });
            if (newPoolId) setSelectedPoolId(newPoolId);
            // Force-sync first so the new pool exists in Mongo before
            // we poll for it — protects against WS handler dropping the
            // PoolCreated projection.
            if (newPoolId) {
                try {
                    await syncReinvestmentPool(newPoolId);
                } catch {
                    /* fall through to waitForPools */
                }
                await waitForPools((latest) => latest.some((p) => p.poolId === newPoolId));
                await loadPoolDetail(newPoolId);
            } else {
                await refreshPools();
            }
            refreshDeposits();
            fetchReinvestmentSettlements({ limit: 20 })
                .then((data) => setSettlements(data?.items || []))
                .catch(() => {});
        } catch (err) {
            failFlow(err, "Create Pool Failed");
        } finally {
            setBusy(false);
        }
    }

    async function handleAttach() {
        if (!signer || busy || !selectedPool || !attachDepositId) return;
        // §V7 (client, Jul 2026) — attach the deposit AS-IS, whatever its
        // Automated Reinvestment (On or Off). The CYR contract gates attach on
        // the IMMUTABLE productMode (Standard/Turbo), NOT compoundMode, so an
        // Off deposit can join a matching pool without being force-enabled
        // first — for both Turbo and Standard pools. (Deposits MINTED from a
        // pool sweep are still created with reinvest ON, so the pool chain
        // keeps compounding.)
        setBusy(true);
        openFlow("Attaching Deposit", [
            {
                key: "attach",
                label: `Attach Term Deposit #${attachDepositId}`,
                doneLabel: "Deposit attached",
                status: "loading"
            }
        ]);
        try {
            const cyr = getContract("reinvestment", signer);
            const overrides = await getTxOverrides(signer.provider);
            const tx = await cyr.attachDeposit(selectedPool.poolId, attachDepositId, overrides);
            await tx.wait();
            markStep("attach", "done");
            await delay(400);
            setFlowOpen(false);
            setResult({
                type: "success",
                title: "Deposit Attached",
                message: `Term Deposit #${attachDepositId} is now compounding daily inside Pool #${selectedPool.poolId}.`
            });
            const justAttached = Number(attachDepositId);
            const targetPoolId = selectedPool.poolId;
            setAttachDepositId(null);
            // Force-sync the backend's pool snapshot against canonical
            // chain state. This guarantees the new attachment is visible
            // even if the WS handler missed the event or its projection
            // failed silently — without this the user can see the tx on
            // BaseScan but the pool stays empty in the UI until the
            // polling indexer happens to re-process it.
            try {
                await syncReinvestmentPool(targetPoolId);
            } catch {
                /* fall through to the polling-based waitForPools */
            }
            // Wait for the WS listener (or our sync above) to add the
            // deposit to the pool's `members` array before refreshing.
            await waitForPools((latest) => {
                const pool = latest.find((p) => p.poolId === targetPoolId);
                return Boolean(pool?.members.includes(justAttached));
            });
            await loadPoolDetail(targetPoolId);
            refreshDeposits();
            fetchReinvestmentSettlements({ limit: 20 })
                .then((data) => setSettlements(data?.items || []))
                .catch(() => {});
        } catch (err) {
            failFlow(err, "Attach Failed");
        } finally {
            setBusy(false);
        }
    }

    /**
     * Step 1 of settle/reinvest: opens the preview modal. The buttons
     * on each pool member row call this; the modal then calls
     * `handleSettleConfirm` if the user confirms. Splitting the flow
     * lets the user back out after seeing the bonus they're about to
     * crystallise.
     */
    function requestSettle(member, mode) {
        if (!signer || busy || !selectedPool) return;
        // Embed principal + product fields here so the modal doesn't
        // need a separate useDeposits lookup. The pool detail API
        // doesn't ship principal / productMode / annualRateBps per
        // member (they're redundant with the wallet's own deposits),
        // so we fold them in from the cached deposit row.
        const sourceDeposit = deposits.find((d) => Number(d.id) === Number(member.depositId));
        const memberWithPrincipal = {
            ...member,
            principal: sourceDeposit?.principal || "0",
            productMode: sourceDeposit?.productMode ?? 0,
            annualRateBps: sourceDeposit?.annualRateBps ?? 2000
        };
        setSettleTarget({ member: memberWithPrincipal, mode, poolId: selectedPool.poolId });
    }

    /**
     * Step 2 of settle/reinvest: fired from the preview modal's
     * Confirm. Submits the chain tx (`manualReinvest` to keep the
     * position pooled, `detachDeposit` to leave the pool entirely).
     */
    async function handleSettleConfirm() {
        if (!signer || busy || !settleTarget || !selectedPool) return;
        const { member, mode } = settleTarget;
        const depositId = member.depositId;
        const isReinvest = mode === "reinvest";
        setSettleTarget(null);
        setBusy(true);
        openFlow(isReinvest ? "Reinvesting" : "Detaching", [
            {
                key: "settle",
                label: isReinvest
                    ? `Materialize bonus for Deposit #${depositId}`
                    : `Settle + detach Deposit #${depositId}`,
                doneLabel: isReinvest ? "Bonus reinvested" : "Deposit detached",
                status: "loading"
            }
        ]);
        try {
            const cyr = getContract("reinvestment", signer);
            const overrides = await getTxOverrides(signer.provider);
            const tx = isReinvest
                ? await cyr.manualReinvest(depositId, overrides)
                : await cyr.detachDeposit(depositId, overrides);
            const receipt = await tx.wait();
            markStep("settle", "done");
            await delay(400);
            setFlowOpen(false);
            setResult({
                type: "success",
                title: isReinvest ? "Bonus Reinvested" : "Deposit Detached",
                message: isReinvest
                    ? `The accrued compound bonus for Deposit #${depositId} was materialized. The position stays in the pool and keeps compounding.`
                    : `Deposit #${depositId} was settled and removed from Pool #${selectedPool.poolId}.`
            });
            const targetPoolId = selectedPool.poolId;
            // Force-sync the pool from chain before any polling so the
            // post-action state (member-list change, lifetime counter
            // bump) is visible immediately even if the WS handler
            // dropped the projection.
            try {
                await syncReinvestmentPool(targetPoolId);
            } catch {
                /* fall through to waitForPools */
            }
            if (isReinvest) {
                // Bonus materialized but the deposit stays attached —
                // wait for the new SettlementExecuted row (at or after
                // this tx's blockNumber) to surface in the settlements
                // feed so lifetime totals + history reflect the entry.
                await waitForSettlement(depositId, receipt.blockNumber);
                await refreshPools();
                await loadPoolDetail(targetPoolId);
            } else {
                // Detach removes the deposit from the pool — wait for
                // the WS listener (or sync above) to drop it from `members`.
                await waitForPools((latest) => {
                    const pool = latest.find((p) => p.poolId === targetPoolId);
                    return !pool?.members.includes(Number(depositId));
                });
                await loadPoolDetail(targetPoolId);
            }
            refreshDeposits();
        } catch (err) {
            failFlow(err, isReinvest ? "Reinvest Failed" : "Detach Failed");
        } finally {
            setBusy(false);
        }
    }

    /**
     * Polls the settlements feed until one with the given depositId at
     * or after `minBlockNumber` appears (or `maxMs` elapses). The block
     * threshold matters because the user may have past settlements for
     * the same depositId — a naive "any row exists" check would return
     * true on the first poll and skip the wait entirely.
     */
    async function waitForSettlement(
        depositId,
        minBlockNumber,
        { intervalMs = 800, maxMs = 12000 } = {}
    ) {
        const target = Number(depositId);
        const minBlock = Number(minBlockNumber);
        const start = Date.now();
        let latest = settlements;
        while (Date.now() - start < maxMs) {
            try {
                const data = await fetchReinvestmentSettlements({ limit: 20 });
                latest = data?.items || [];
                setSettlements(latest);
                const hit = latest.some(
                    (s) => Number(s.depositId) === target && Number(s.blockNumber) >= minBlock
                );
                if (hit) return latest;
            } catch {
                /* transient — keep polling */
            }
            await delay(intervalMs);
        }
        return latest;
    }

    function failFlow(err, title) {
        setFlowMeta((prev) => ({
            ...prev,
            steps: prev.steps.map((s) => (s.status === "loading" ? { ...s, status: "error" } : s))
        }));
        setTimeout(() => {
            setFlowOpen(false);
            setResult({ type: "error", title, message: decodeContractError(err) });
        }, 300);
    }

    /* ---------------------------- render --------------------------- */

    if (!address) {
        return (
            <div className="-mt-6 flex min-h-[60vh] items-center justify-center">
                <div className="rounded-[14px] bg-[#10281b] ring-1 ring-[#bfb67b] px-8 py-10 text-center max-w-md">
                    <h2 className="font-manrope text-[18px] font-bold text-[#e6c985]">Wallet Required</h2>
                    <p className="mt-2 text-[12px] text-[#e6c985]/80">
                        Connect your wallet to use Consolidated Yield Reinvestment.
                    </p>
                </div>
            </div>
        );
    }

    const lifetimeTotal = pools.reduce((acc, p) => acc + BigInt(p.lifetimeMaterialized || 0), 0n);
    const attachedCount = attachedSet.size;
    const stats = [
        {
            label: "My Pools",
            value: String(pools.length),
            unit: "",
            delta: pools.length > 0 ? "Active" : "None",
            iconSrc: STAT_ICONS.holdings
        },
        {
            label: "Attached Deposits",
            value: String(attachedCount),
            unit: "",
            delta: `${eligibleDeposits.length} eligible`,
            iconSrc: STAT_ICONS.active
        },
        {
            label: "Lifetime Reinvested",
            value: `$${formatUsdcCompact(lifetimeTotal)}`,
            unit: TOKEN_SYMBOL,
            delta: "Compounded bonus",
            iconSrc: STAT_ICONS.safe
        }
    ];

    const members = poolDetail?.members || [];
    const pendingLiability = poolDetail?.pendingLiability || "0";
    // §I.6 — the sweep trigger checks the SUM of every member's
    // `previewAccruedInterest`. The threshold meter must track that,
    // not `pendingLiability` (which was the now-removed compound BONUS
    // figure — kept as a fallback for older backend builds).
    const aggregateAccruedYield =
        poolDetail?.aggregateAccruedYield || pendingLiability;
    // §M6 — combined DAILY accrual across the pool's members, used to project
    // "#days until new TD created" on the threshold meter. Each member earns
    // its own stored APR on its own principal.
    const poolDailyYield = useMemo(() => {
        let perDay = 0n;
        for (const m of members) {
            const principal = BigInt(m.principal || m.principalSnapshot || 0);
            if (principal === 0n) continue;
            const rateBps = BigInt(
                Number(m.annualRateBps) || config.protocol.annualRateBps
            );
            perDay += (principal * rateBps) / (10000n * 365n);
        }
        return perDay;
    }, [members]);

    // Lookup a deposit's principal by id. Used to surface the original
    // deposit amount on each pool member row — the backend API doesn't
    // ship principal in the pool detail payload, but `useDeposits` is
    // already loaded for the wallet's positions so we can read it
    // from there.
    function memberPrincipalOf(depList, depositId) {
        if (!depList || depList.length === 0) return "0";
        const match = depList.find((d) => Number(d.id) === Number(depositId));
        return match?.principal || "0";
    }

    return (
        <div className="flex flex-col gap-3">

            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {stats.map((item) => (
                    <StatCard key={item.label} {...item} loading={poolsLoading} />
                ))}
            </div>

            <div className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] px-4 py-2.5">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <h3 className="shrink-0 font-manrope text-[12px] font-bold uppercase tracking-widest text-[#e6c985] sm:pr-4 sm:border-r sm:border-white/15">
                        How Auto-Reinvest works
                    </h3>
                    <ul className="grid flex-1 grid-cols-1 gap-x-4 gap-y-1.5 text-[10.5px] leading-snug text-white sm:grid-cols-3 sm:pl-4">
                        <Bullet>Yield settlement is deferred</Bullet>
                        <Bullet>Sweep mints a fresh compounded TD</Bullet>
                        <Bullet>Detach to unlock the deposit</Bullet>
                    </ul>
                </div>
            </div>

            <div className="flex flex-col gap-3 lg:flex-row">
                {/* Pools + selected-pool members */}
                <section className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4 lg:w-[58.333%]">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">
                            My Auto-Reinvest Pools
                        </h3>
                        <div className="flex items-center gap-1.5">
                            <button
                                type="button"
                                disabled={busy}
                                onClick={() => handleCreatePool(0)}
                                title="Standard pool: yield → plain new deposit on sweep"
                                className="inline-flex items-center gap-1.5 rounded-md bg-[#FBC206] px-3 py-1.5 text-[11px] font-semibold text-[#10281b] hover:-translate-y-0.5 transition-all disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <PlusIcon />
                                Standard
                            </button>
                            <button
                                type="button"
                                disabled={busy}
                                onClick={() => handleCreatePool(1)}
                                title="Turbo pool: on sweep, the yield mints a fresh Turbo base deposit that then runs the leverage loop (each rung borrows ~60%); every rung is auto-attached to the pool"
                                className="inline-flex items-center gap-1.5 rounded-md bg-[#10281b] px-3 py-1.5 text-[11px] font-semibold text-[#FBC206] ring-1 ring-[#FBC206] hover:-translate-y-0.5 transition-all disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <PlusIcon />
                                Turbo
                            </button>
                        </div>
                    </div>

                    {/* §M6 (Jul 2026 client review) — "It's not clear here what
                        the Standard & Turbo are for." The two buttons create a
                        pool of that type; the type decides ONLY what the swept
                        yield mints, never which deposits can feed it. */}
                    <p className="mt-1.5 text-[10.5px] leading-snug text-[#e6c985]/70">
                        A pool sweeps the interest from every deposit you attach to it and turns
                        it into one new Term Deposit. <strong className="text-[#e6c985]">Standard</strong>{" "}
                        and <strong className="text-[#e6c985]">Turbo</strong> only decide what that
                        new deposit becomes — not which deposits can feed it. Any Active Term
                        Deposit can be attached to either kind of pool. Standard sweeps once
                        combined yield passes ${config.protocol.autoCompoundTriggerUsdc}; Turbo
                        waits for $
                        {config.protocol.turboCompoundTriggerUsdc.toLocaleString("en-US")}, because
                        its leverage loop needs the size to work.
                    </p>

                    <div className="mt-3 flex flex-wrap gap-2">
                        {poolsLoading && pools.length === 0
                            ? Array.from({ length: 2 }).map((_, i) => (
                                  <Shimmer key={`pskel-${i}`} width="7rem" height="2.25rem" />
                              ))
                            : null}
                        {!poolsLoading && pools.length === 0 ? (
                            <p className="rounded-[10px] bg-white/10 ring-1 ring-[#bfb67b] px-3 py-3 text-[11px] text-[#e6c985]/70">
                                No pools yet. Create one to start consolidating deposit yield.
                            </p>
                        ) : null}
                        {pools.map((pool) => {
                            const active = (selectedPool?.poolId || null) === pool.poolId;
                            const isTurbo = pool.poolType === 1 || pool.poolTypeLabel === "Turbo";
                            return (
                                <button
                                    key={pool.poolId}
                                    type="button"
                                    onClick={() => setSelectedPoolId(pool.poolId)}
                                    className={`rounded-[10px] px-3 py-2 text-left ring-1 transition-colors ${
                                        active
                                            ? "bg-[#FBC206] text-[#10281b] ring-[#FBC206]"
                                            : "bg-white/10 text-white ring-[#bfb67b] hover:bg-white/20"
                                    }`}
                                >
                                    <div className="flex items-center gap-1.5">
                                        <span className="font-manrope text-[12.5px] font-bold">
                                            Pool #{pool.poolId}
                                        </span>
                                        <span
                                            className={`rounded-full px-1.5 py-0.5 text-[8.5px] font-bold uppercase tracking-widest ${
                                                isTurbo
                                                    ? active
                                                        ? "bg-[#10281b] text-[#FBC206]"
                                                        : "bg-[#FBC206]/20 text-[#FBC206]"
                                                    : active
                                                    ? "bg-[#10281b]/20 text-[#10281b]"
                                                    : "bg-white/15 text-white/85"
                                            }`}
                                        >
                                            {isTurbo ? "Turbo" : "Standard"}
                                        </span>
                                    </div>
                                    <div
                                        className={`text-[10px] ${
                                            active ? "text-[#10281b]/75" : "text-[#e6c985]/65"
                                        }`}
                                    >
                                        {pool.memberCount} attached
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {selectedPool ? (
                        <div className="mt-4">
                            <div className="flex items-center justify-between">
                                <h4 className="font-manrope text-[11px] font-bold uppercase tracking-widest text-[#e6c985]">
                                    Pool #{selectedPool.poolId} — Attached Deposits
                                </h4>
                                {/* §V6.2 I.6 — "Pending liability" was the
                                    aggregate of the (now-removed) Pool Bonus
                                    figures. Spec model is a pure total-value
                                    sweep of accrued yield, no separate
                                    Treasury-funded bonus. */}
                            </div>
                            <PoolThresholdMeter
                                pendingLiability={aggregateAccruedYield}
                                poolType={selectedPool?.poolType ?? 0}
                                keeperFeeEstimate={keeperFeeEstimate}
                                dailyYield={poolDailyYield}
                            />


                            <ul className="mt-2 space-y-2">
                                {detailLoading && members.length === 0 ? (
                                    <li>
                                        <Shimmer width="100%" height="3.5rem" />
                                    </li>
                                ) : null}
                                {!detailLoading && members.length === 0 ? (
                                    <li className="rounded-[10px] bg-white/10 ring-1 ring-[#bfb67b] px-3 py-3 text-center text-[11px] text-[#e6c985]/70">
                                        No deposits attached to this pool yet.
                                    </li>
                                ) : null}
                                {members.map((member) => (
                                    <li
                                        key={member.depositId}
                                        className="rounded-[10px] bg-white/10 ring-1 ring-[#bfb67b] px-3 py-2.5"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="font-manrope text-[12px] font-bold text-white">
                                                Term Deposit #{member.depositId}
                                            </div>
                                            <div className="text-[10px] text-[#e6c985]/70">
                                                {Number(member.daysCompounded || 0)} days in pool
                                            </div>
                                        </div>
                                        {/* §V6.2 I.6 — only Principal + Accrued
                                            Interest are part of the spec. The
                                            earlier "Projected" + "Pool bonus"
                                            metrics surfaced the contract's
                                            compound-vs-simple delta, which is
                                            not in the V6.2 Final spec (sweep
                                            model is a flat "total value sweep"
                                            of accrued yield). */}
                                        <div className="mt-1.5 grid grid-cols-2 gap-2 text-[10px]">
                                            <Metric
                                                label="Principal"
                                                value={`$${formatUsdcCompact(memberPrincipalOf(deposits, member.depositId))}`}
                                                tooltip="Original deposit amount, fixed at open. Unchanged by pool attach."
                                            />
                                            <Metric
                                                label="Accrued interest"
                                                value={`$${formatUsdcCompact(member.accruedInterest || "0")}`}
                                                highlight
                                                tooltip="Total interest the deposit has earned since it was OPENED — pre-attach plus post-attach. This is the figure the keeper sweeps into the pool when the threshold trips."
                                            />
                                        </div>
                                        <div className="mt-2">
                                            {/* §V6.2 I.6 — manual "Reinvest Bonus" is
                                                not in spec ("Lazy settlement is
                                                explicitly excluded. Execution operates
                                                through keeper-based automation").
                                                Detach IS in spec ("detach anytime"). */}
                                            <button
                                                type="button"
                                                disabled={busy}
                                                onClick={() => requestSettle(member, "detach")}
                                                className="w-full rounded-md bg-white/10 ring-1 ring-[#bfb67b] px-2 py-1.5 text-[10.5px] font-semibold text-white hover:bg-white/20 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                Detach
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : null}
                </section>

                {/* Attach + settlement history */}
                <section className="flex flex-col gap-3 lg:w-[41.666%]">
                    <div className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                        <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">
                            Attach a Deposit
                        </h3>
                        <p className="mt-1 text-[10px] text-[#e6c985]/70">
                            {selectedPool
                                ? `Into Pool #${selectedPool.poolId} — this list is the same for Standard and Turbo pools`
                                : "Create a pool first"}
                        </p>

                        <ul className="mt-3 max-h-[220px] space-y-2 overflow-y-auto p-0.5">
                            {depositsLoading && eligibleDeposits.length === 0 ? (
                                <li>
                                    <Shimmer width="100%" height="2.5rem" />
                                </li>
                            ) : null}
                            {!depositsLoading && eligibleDeposits.length === 0 ? (
                                <li className="rounded-[10px] bg-white/10 ring-1 ring-[#bfb67b] px-3 py-3 text-center text-[11px] text-[#e6c985]/70">
                                    No eligible deposits. Only Active, un-pooled term
                                    deposits can be attached.
                                </li>
                            ) : null}
                            {eligibleDeposits.map((d) => {
                                const sel = attachDepositId === d.id;
                                // §V6.2 — Product is the IMMUTABLE flag
                                // (Standard / Turbo, set at deposit
                                // open). Automated Reinvestment is the mutable
                                // toggle. Both shown so user sees the
                                // deposit's full state. Attaching a
                                // deposit whose Automated Reinvestment is Off
                                // auto-enables it in handleAttach
                                // before the on-chain call.
                                const isTurbo = Number(d.productMode) === 1;
                                const productLabel = isTurbo ? "Turbo" : "Standard";
                                const productTone = sel
                                    ? isTurbo
                                        ? "bg-[#10281b] text-[#FBC206]"
                                        : "bg-[#10281b]/20 text-[#10281b]"
                                    : isTurbo
                                    ? "bg-[#FBC206]/20 text-[#FBC206]"
                                    : "bg-white/15 text-white/85";
                                const autoOn = Number(d.compoundMode) > 0;
                                const autoTone = sel
                                    ? autoOn
                                        ? "bg-[#10281b]/15 text-[#10281b]"
                                        : "bg-[#10281b]/15 text-[#10281b]/70"
                                    : autoOn
                                    ? "bg-[#22C55E]/20 text-[#22C55E]"
                                    : "bg-white/10 text-white/65";
                                return (
                                    <li key={d.id}>
                                        <button
                                            type="button"
                                            onClick={() => setAttachDepositId(d.id)}
                                            className={`flex w-full items-center justify-between gap-2 rounded-[10px] px-3 py-2 text-left ring-1 transition-colors ${
                                                sel
                                                    ? "bg-[#FBC206] text-[#10281b] ring-[#FBC206]"
                                                    : "bg-white/10 text-white ring-[#bfb67b] hover:bg-white/20"
                                            }`}
                                        >
                                            <div className="flex items-center gap-2 min-w-0 flex-wrap">
                                                <span className="font-manrope text-[12px] font-bold whitespace-nowrap">
                                                    Term Deposit #{d.id}
                                                </span>
                                                <span
                                                    className={`rounded-full px-1.5 py-0.5 text-[8.5px] font-bold uppercase tracking-widest ${productTone}`}
                                                >
                                                    {productLabel}
                                                </span>
                                                <span
                                                    className={`rounded-full px-1.5 py-0.5 text-[8.5px] font-bold uppercase tracking-widest ${autoTone}`}
                                                    title={autoOn ? "Automated Reinvestment is on" : "Automated Reinvestment is off — will be enabled when you attach"}
                                                >
                                                    {autoOn ? "On" : "Off"}
                                                </span>
                                            </div>
                                            <span
                                                className={`text-[11px] font-bold whitespace-nowrap ${
                                                    sel ? "text-[#10281b]" : "text-[#e6c985]"
                                                }`}
                                            >
                                                ${formatUsdcCompact(d.principal)}
                                            </span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>

                        <button
                            type="button"
                            disabled={busy || !selectedPool || !attachDepositId}
                            onClick={handleAttach}
                            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#FBC206] px-4 py-2.5 font-manrope text-[13px] font-semibold text-[#10281b] hover:-translate-y-0.5 transition-all disabled:cursor-not-allowed disabled:opacity-50 disabled:translate-y-0"
                        >
                            {busy ? "Submitting..." : "Attach to Pool"}
                        </button>
                    </div>

                    <div className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4">
                        <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">
                            Recent Settlements
                        </h3>
                        <ul className="mt-2 space-y-1.5">
                            {settlements.length === 0 ? (
                                <li className="text-[11px] text-[#e6c985]/70">
                                    No materialized settlements yet.
                                </li>
                            ) : null}
                            {settlements.slice(0, 6).map((s) => (
                                <li
                                    key={`${s.txHash}-${s.logIndex}`}
                                    className="flex items-center justify-between border-b border-white/5 pb-1.5 text-[10.5px] last:border-0"
                                >
                                    <span className="text-white">
                                        Pool #{s.poolId} · TD #{s.depositId}{" "}
                                        <span className="text-[#e6c985]/60">({s.reasonLabel})</span>
                                    </span>
                                    <span className="font-semibold text-[#FBC206]">
                                        +${formatUsdcCompact(s.bonusMaterialized)}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            </div>

            <FlowModal
                open={flowOpen}
                title={flowMeta.title}
                subtitle="Confirm the transaction in your wallet."
                steps={flowMeta.steps}
            />

            <SettlementPreviewModal
                state={settleTarget}
                busy={busy}
                onClose={() => setSettleTarget(null)}
                onConfirm={handleSettleConfirm}
            />

            <ResultModal
                open={result !== null}
                type={result?.type}
                title={result?.title || ""}
                message={result?.message || ""}
                onClose={() => setResult(null)}
            />
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * §V6.2 threshold meter — visualises the pool's aggregate yield against
 * the $100 Automated Reinvestment sweep trigger. The KEEPER FEE is dynamic (gas
 * × ETH/USD, capped by governance) and DEDUCTED from the swept yield
 * at execution time — it does not raise the threshold. The meter shows
 * the live fee estimate inline so the user knows roughly what slice
 * of the swept yield will pay for execution.
 */
function PoolThresholdMeter({ pendingLiability, poolType, keeperFeeEstimate, dailyYield = 0n }) {
    const isTurbo = poolType === 1 || poolType === "Turbo";
    // §M6 (Jul 2026 client review) — "Turbo doesn't work very well below
    // $10k. And at $100 it doesn't work at all. So perhaps Standard sweeps at
    // $100 and Turbo only sweeps when it reaches $10,000?" Actions (b)/(c).
    // Mirrors ConsolidatedYieldReinvestment.sweepThresholdFor().
    const threshold = BigInt(
        isTurbo
            ? config.protocol.turboCompoundTriggerUsdc
            : config.protocol.autoCompoundTriggerUsdc
    ) * 1_000_000n;
    let pending = 0n;
    try {
        pending = BigInt(pendingLiability || "0");
    } catch {
        pending = 0n;
    }
    const ratio = pending >= threshold ? 1 : Number(pending) / Number(threshold);
    const reached = pending >= threshold;
    const remaining = reached ? 0n : threshold - pending;
    const thresholdLabel = `$${Number(threshold / 1_000_000n).toLocaleString("en-US")}`;
    // §M6 — "maybe #days until new TD created". Projected from the pool's
    // current daily accrual; null while the pool has no members earning yet.
    const daysToSweep =
        !reached && dailyYield > 0n
            ? Math.ceil(Number(remaining) / Number(dailyYield))
            : null;

    // Dynamic fee: backend ships the current estimate (gas × ETH/USD,
    // capped). Falls back to "—" while the first poll resolves; the
    // user-facing copy adapts so we never quote a stale "$2".
    const feeAvailable = Boolean(keeperFeeEstimate?.available);
    const feeUsdc = feeAvailable ? BigInt(keeperFeeEstimate.keeperFeeUsdc || "0") : null;
    const feeLabel = feeUsdc != null ? `$${formatUsdcCompact(feeUsdc)}` : "—";

    return (
        <div className="mt-2 rounded-[10px] bg-white/5 ring-1 ring-[#bfb67b]/40 px-3 py-2.5">
            <div className="flex items-center justify-between text-[10px] text-[#e6c985]/80">
                <span>
                    {thresholdLabel} sweep threshold
                    <span className="text-[#e6c985]/60">
                        {" "}(keeper fee {feeLabel}, deducted from yield)
                    </span>
                </span>
                <span
                    className={`font-manrope font-semibold ${reached ? "text-[#22C55E]" : "text-[#FBC206]"}`}
                >
                    {reached
                        ? isTurbo
                            ? "Ready: will mint a Turbo base + leverage loop"
                            : "Ready: will mint a fresh Standard deposit"
                        : `$${formatUsdcCompact(remaining)} until sweep`}
                </span>
            </div>
            {daysToSweep != null ? (
                <div className="mt-1 text-[9.5px] text-[#e6c985]/60">
                    At the current accrual rate that&rsquo;s about{" "}
                    <span className="font-semibold text-[#e6c985]">
                        {daysToSweep === 1 ? "1 day" : `${daysToSweep.toLocaleString("en-US")} days`}
                    </span>{" "}
                    until the next Term Deposit is created.
                </div>
            ) : null}
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/10">
                <div
                    className="h-full rounded-full transition-all"
                    style={{
                        width: `${Math.max(2, Math.min(100, ratio * 100)).toFixed(1)}%`,
                        backgroundColor: reached ? "#22C55E" : "#FBC206"
                    }}
                />
            </div>
            <p className="mt-1.5 text-[9.5px] leading-relaxed text-[#e6c985]/60">
                {isTurbo
                    ? `Once the pool hits ${thresholdLabel} of combined yield, the keeper sweeps the full balance. The dynamic keeper fee (${feeLabel} at current gas) is deducted, and the remaining yield mints a fresh Turbo base deposit. That base then runs the Turbo leverage loop — each rung borrows ~60% of the one before it — and every rung is auto-attached to this pool, so the whole leveraged stack keeps compounding inside it. Turbo needs the higher threshold because each rung has to clear the $${config.protocol.minDepositUsdc} protocol minimum; below that the loop stalls and the position is Turbo in name only.`
                    : `Once the pool hits ${thresholdLabel} of combined yield, the keeper sweeps the full balance. The dynamic keeper fee (${feeLabel} at current gas) is deducted, and the remainder mints a fresh Standard deposit, auto-attached.`}
            </p>
        </div>
    );
}

function Metric({ label, value, highlight = false, tooltip = null }) {
    return (
        <div className="rounded-md bg-[#162e1f] ring-1 ring-[#bfb67b]/60 px-2 py-1">
            <div className="flex items-center gap-1 text-[8.5px] uppercase tracking-wide text-[#e6c985]/60">
                <span>{label}</span>
                {tooltip ? (
                    // Native `title` attribute = browser tooltip. Lightweight
                    // (no JS), accessible (screen readers read it), and
                    // sufficient for the bonus-formula clarification. Drop
                    // in a custom popover later if richer formatting needed.
                    <span
                        className="inline-flex h-3 w-3 cursor-help items-center justify-center rounded-full bg-[#e6c985]/20 text-[8px] font-bold text-[#e6c985]"
                        title={tooltip}
                        aria-label={tooltip}
                    >
                        ?
                    </span>
                ) : null}
            </div>
            <div className={`font-manrope text-[11px] font-bold ${highlight ? "text-[#FBC206]" : "text-white"}`}>
                {value}
            </div>
        </div>
    );
}

function Bullet({ children }) {
    return (
        <li className="flex items-start gap-1.5">
            <span className="mt-0.5 inline-flex h-3 w-3 shrink-0 items-center justify-center rounded-full bg-[#FBC206] text-[7px] font-bold text-[#10281b]">
                ✓
            </span>
            <span>{children}</span>
        </li>
    );
}

function PlusIcon() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true">
            <line x1="10" y1="4" x2="10" y2="16" />
            <line x1="4" y1="10" x2="16" y2="10" />
        </svg>
    );
}

function CycleIcon() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="21 4 21 10 15 10" />
            <polyline points="3 20 3 14 9 14" />
            <path d="M19.4 9A8 8 0 0 0 6.3 5.3L3 8m18 8-3.3 2.7A8 8 0 0 1 4.6 15" />
        </svg>
    );
}